import type { ISODateTime } from './inventory';

/**
 * Field Ops teams and their queue owners (issue #7957). EUROPE (the Norway
 * team, covering every non-Chile site) is the default; Chile is the other.
 * The queue-owner model mirrors the Opportunity project's contract
 * signers: effective-dated assignments with an operational "available"
 * (out-of-office) switch and an "active" switch, resolved with a fallback
 * to a cross-team backup. (The `NORWAY` enum value was renamed to `EUROPE`
 * -- it always covered Norway / Scotland / Faroe / any non-Chile site.)
 */

export const fieldOpsTeams = ['EUROPE', 'CHILE'] as const;
export type FieldOpsTeam = (typeof fieldOpsTeams)[number];

export const DEFAULT_TEAM: FieldOpsTeam = 'EUROPE';

export const teamLabels: Record<FieldOpsTeam, string> = {
  EUROPE: 'Europe',
  CHILE: 'Chile',
};

/**
 * Suggested team for a new request, from the leatherback site's
 * `labels.country`. Decided rule (issue #7957 follow-up): a site in Chile
 * goes to the Chile queue; everything else — including sites with no
 * country label — is the Europe queue. Always overridable in the form.
 */
export function teamForCountry(country: string | undefined): FieldOpsTeam {
  return (country ?? '').trim().toLowerCase() === 'chile' ? 'CHILE' : DEFAULT_TEAM;
}

// Queue-owner assignment scope: a specific team, or ALL for a cross-team
// backup owner.
export const queueOwnerScopes = ['EUROPE', 'CHILE', 'ALL'] as const;
export type QueueOwnerScope = (typeof queueOwnerScopes)[number];

export const queueOwnerScopeLabels: Record<QueueOwnerScope, string> = {
  EUROPE: 'Europe',
  CHILE: 'Chile',
  ALL: 'All teams (backup)',
};

export interface FieldOpsQueueOwner {
  id: string;
  displayName: string;
  email: string;
  scope: QueueOwnerScope;
  priority: number;
  available: boolean;
  active: boolean;
  effectiveFrom?: ISODateTime | undefined;
  effectiveUntil?: ISODateTime | undefined;
  version: number;
  lastModified?: ISODateTime | undefined;
  lastModifiedBy?: string | undefined;
}

export type FieldOpsQueueOwnerInput = {
  displayName: string;
  email: string;
  scope: QueueOwnerScope;
  priority: number;
  available: boolean;
  active: boolean;
};

export type FieldOpsQueueOwnerPatch = {
  id: string;
  version: number;
} & Partial<Pick<FieldOpsQueueOwner, 'displayName' | 'email' | 'scope' | 'priority' | 'available' | 'active'>>;

export type QueueOwnerResolution = 'REGIONAL' | 'BACKUP';

export interface ResolvedQueueOwner {
  owner: FieldOpsQueueOwner;
  resolution: QueueOwnerResolution;
}
